import { FC, useEffect, useState } from 'react';
import { OnboardingEye } from '../Eye';
import BPContainer from 'components/BionicUI/container/BPContainer';
import BPButton from 'components/BionicUI/button/BPButton';
import BPInput from 'components/BionicUI/input/BPInput';
import BPSpinner from 'components/BionicUI/loading/BPSpinner';
import {
  GetAppConfigByKeyRequest,
  KycTokenCriteria,
} from 'core/models/app-config/get-app-config-by-key.model';
import { useEth } from 'context/eth.context';
import { UserV1BaseServerRepo } from 'core/repository/base-server/v1/user.repo';
import { AppConfigurationKey } from 'core/models/app-config/get-app-config.model';
import { AppConfigV1BaseServerRepo } from 'core/repository/base-server/v1/app-config.repo';
import { formatWalletAddress } from 'utils/format';
import commaNumber from 'comma-number';
import BPInstruction from '../../BionicUI/instruction/BPInstruction';
import { SequenceItem } from './OnboardingKYCSequence';
import InputBar from '../InputBar';
import {
  GetUserKYCRequest,
  GetUserKYCResponse,
} from 'core/models/users/kyc/get-user-kyc.model';
import { KYCStatus } from 'core/models/users/kyc/user-kyc.model';
import {
  GetUserMainWalletRequest,
  GetUserMainWalletResponse,
} from 'core/models/users/wallet/get-user-main-wallet.model';
import { useAuth } from 'context/auth.context';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes';
import ProgressBar from './ProgressBar';
import WarningBar from '../WarningBar';
import LogoBarStatic from '../LogoBarStatic';
import BackgroundVideo from '../BackgroundVideo';
import Vault from '../Vault';
import { usePrivy } from '@privy-io/react-auth';
// import { KYCV1BaseServerRepo } from 'core/repository/base-server/v1/kyc.repo';

const KYCResult: FC = () => {
  const [walletDetails, setWalletDetails] =
    useState<GetUserMainWalletResponse>();
  const [KYCCriteria, setKYCCriteria] = useState<KycTokenCriteria>();
  const [KYCResponse, setKYCResponse] = useState<GetUserKYCResponse>();
  const [KYCSuccess, setKYCSuccess] = useState<boolean>(false);
  const [userBalance, setUserBalance] = useState<number>(0);
  // const [rejectionMessage, setRejectionMessage] = useState<string>('');
  const { handleProfileCreationOrLoad } = useAuth();
  const { user } = usePrivy();
  const navigate = useNavigate();
  const { getWalletBcnxBalance } = useEth();
  // console.log('$$$$$$$$$$$$$$$$$$', walletDetails, KYCResponse, userBalance);
  function checkBalanceValidation(balance: number): boolean {
    return KYCCriteria?.amount ? balance >= KYCCriteria.amount : false;
  }

  async function fetchUserBalance() {
    try {
      if (walletDetails?.address) {
        const balance = await getWalletBcnxBalance(
          KYCCriteria!,
          walletDetails.address
        );
        setUserBalance(balance);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (!walletDetails || checkBalanceValidation(userBalance!)) return;

    fetchUserBalance();

    const interval = setInterval(() => {
      fetchUserBalance();
    }, 5000);

    return () => clearInterval(interval);
  }, [walletDetails]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const request: GetAppConfigByKeyRequest = {
          key: AppConfigurationKey.KYC_CRITERIA,
        };
        const response = await new AppConfigV1BaseServerRepo().getByKey(
          request
        );
        setKYCCriteria(response[AppConfigurationKey.KYC_CRITERIA]);

        const walletRequest: GetUserMainWalletRequest = {};
        const walletResponse = await new UserV1BaseServerRepo().getMainWallet(
          walletRequest
        );
        setWalletDetails(walletResponse);

        const kycRequest: GetUserKYCRequest = {};
        const kycResponse = await new UserV1BaseServerRepo().getKYC(kycRequest);
        setKYCResponse(kycResponse);
        setKYCSuccess(kycResponse?.status === KYCStatus.APPROVED);
        // if (kycResponse?.status !== KYCStatus.APPROVED) {
        //   const kycDetails = await new KYCV1BaseServerRepo().updateKYC({});
        //   if (kycResponse?.status === KYCStatus.REJECTED) {
        //     setRejectionMessage(kycDetails.message ? kycDetails.message : '');
        //   }
        // }
      } catch (error) {
        console.log(error);
      }
    };

    if (!user) return;

    fetchData();
  }, [user]);

  async function onHandleContinue() {
    try {
      await handleProfileCreationOrLoad();
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="grid w-full h-full place-content-center">
      <BackgroundVideo />
      <Vault fadeInImmediately />

      <div className="grid grid-rows-[auto_auto_1fr] gap-[8px] absolute top-0 left-0 w-screen h-full text-center justify-center">
        <WarningBar />
        <LogoBarStatic />

        <div className="grid w-full h-full grid-rows-[1fr_auto] gap-[30px] pb-[30px]">
          <SequenceItem>
            {KYCResponse && (
              <>
                <div className="inline-grid w-full max-w-[800px] px-6 grid-cols-[auto_1fr] gap-[20px] mx-auto pt-[15vh] items-start">
                  <OnboardingEye />
                  <BPInstruction
                    line1={{
                      text: KYCSuccess
                        ? "Your KYC was successful. Let's complete your onboarding."
                        : KYCResponse.status === KYCStatus.PENDING ||
                            KYCResponse.status === KYCStatus.CONTACTED
                          ? 'Your KYC is in review'
                          : 'Unfortunately, your KYC failed',
                      sound: KYCSuccess
                        ? 'kyc/kyc-successful_1.mp3'
                        : KYCResponse.status === KYCStatus.PENDING
                          ? ''
                          : 'kyc/unfortunately-your-kyc-failed_1.mp3',
                    }}
                    showContentImmediately={true}
                  >
                    <div className="grid relative gap-4 text-[14px] font-medium">
                      {
                        <div className="animate-fade-in">
                          <div className="grid grid-cols-[auto_1fr] gap-3 items-center absolute -top-[80px]  text-bionic-muted text-[12px]">
                            <div>
                              <BPSpinner width={20} />
                            </div>
                            <div className="animate-pulse">
                              Wallet is being checked every 5 seconds
                            </div>
                          </div>
                          {KYCSuccess ? null : KYCResponse.status ===
                            KYCStatus.PENDING ? (
                            <div className="text-bionic">
                              Your KYC application is under review. Once
                              approved, you will be able to invest
                            </div>
                          ) : KYCResponse.status === KYCStatus.CONTACTED ? (
                            <div className="text-bionic">
                              Fractal has sent you an email. Please check your
                              inbox or spam folder
                            </div>
                          ) : KYCResponse.status === KYCStatus.REJECTED ? (
                            <div className="text-bionic-error">
                              You can update your application by visiting the{' '}
                              <a
                                href="https://app.fractal.id/"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Fractal
                              </a>{' '}
                              site. You must log in using the same email
                              (recommended) or wallet that you used previously
                            </div>
                          ) : null}
                          <div className="grid relative w-[92%] grid-cols-[1fr_0.65fr] grid-rows-[auto_1fr_auto] gap-y-1 gap-x-4 pt-6">
                            <div className="text-bionic-muted text-[12px]">
                              Connected wallet
                            </div>
                            <div className="text-bionic-muted text-[12px]">
                              Connected wallet balance
                            </div>
                            <div className="pt-1 pb-0 pointer-events-none">
                              <BPInput
                                state={
                                  KYCSuccess
                                    ? 'success'
                                    : KYCResponse.status === 'PENDING'
                                      ? 'default'
                                      : 'error'
                                }
                                value={formatWalletAddress(
                                  walletDetails?.address!,
                                  23
                                )}
                                twWidth="w-[100%]"
                                icon="wallet"
                              />
                            </div>
                            <div className="pt-1 pb-0 pointer-events-none">
                              <BPInput
                                state={
                                  KYCSuccess
                                    ? 'success'
                                    : KYCResponse.status === 'PENDING'
                                      ? 'default'
                                      : 'error'
                                }
                                value={`${commaNumber(userBalance) ?? ''} ${KYCCriteria?.symbol}`}
                                twWidth="w-[100%]"
                                currency="bcnx"
                              />
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </BPInstruction>
                </div>
                {
                  <InputBar>
                    <BPContainer twWidth="w-auto md:w-[644px] justify-normal">
                      {KYCSuccess ? (
                        <BPButton state="primary" onClick={onHandleContinue}>
                          Continue to Dashboard
                        </BPButton>
                      ) : KYCResponse.status === 'PENDING' ? (
                        <BPButton
                          state="primary"
                          onClick={() => navigate(ROUTES.APPLICATION_IN_REVIEW)}
                        >
                          Go to dashboard
                        </BPButton>
                      ) : (
                        <BPButton
                          state="primary"
                          onClick={() => navigate(ROUTES.APPLICATION_IN_REVIEW)}
                        >
                          Contact Support
                        </BPButton>
                      )}
                    </BPContainer>
                  </InputBar>
                }
              </>
            )}
          </SequenceItem>

          <div>
            <ProgressBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCResult;
